import React, { useEffect, useState, type CSSProperties } from 'react';
import type { EditorNotebookNote } from '../types';
import {
  createEditorNotebookNote,
  deleteEditorNotebookNote,
  getEditorNotebook,
  updateEditorNotebookNote,
} from '../api';
import { PlusIcon, SearchIcon, TrashIcon } from './EditorIcons';

interface EditorNotebookOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Academic notebook — cab-overlay chrome + cabinet-* body. */
export const EditorNotebookOverlay: React.FC<EditorNotebookOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  const [notes, setNotes] = useState<EditorNotebookNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [selectedNote, setSelectedNote] = useState<EditorNotebookNote | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formCategory, setFormCategory] = useState('STRATEGIC');
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formIsDraft, setFormIsDraft] = useState(false);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const data = await getEditorNotebook();
      const safeData = Array.isArray(data) ? data : [];
      setNotes(safeData);
      if (safeData.length > 0 && !selectedNote) {
        setSelectedNote(safeData[0]);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      void fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const categories = [
    { key: 'ALL', label: 'Все заметки' },
    { key: 'STRATEGIC', label: 'Стратегические' },
    { key: 'DECISIONS', label: 'Решения' },
    { key: 'DRAFTS', label: 'Черновики' },
  ];

  const filteredNotes = (notes || []).filter((n) => {
    if (activeCategory === 'STRATEGIC' && n.category !== 'STRATEGIC') return false;
    if (activeCategory === 'DECISIONS' && n.category !== 'DECISIONS') return false;
    if (activeCategory === 'DRAFTS' && !n.is_draft && n.category !== 'DRAFTS') return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q);
    }
    return true;
  });

  const handleStartCreate = () => {
    setIsEditing(true);
    setSelectedNote(null);
    setFormCategory('STRATEGIC');
    setFormTitle('');
    setFormContent('');
    setFormIsDraft(false);
  };

  const handleStartEdit = (note: EditorNotebookNote) => {
    setIsEditing(true);
    setSelectedNote(note);
    setFormCategory(note.category);
    setFormTitle(note.title);
    setFormContent(note.content);
    setFormIsDraft(note.is_draft);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    try {
      if (selectedNote?.id) {
        const updated = await updateEditorNotebookNote(selectedNote.id, {
          title: formTitle,
          content: formContent,
          category: formCategory,
          is_draft: formIsDraft,
        });
        setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
        setSelectedNote(updated);
      } else {
        const created = await createEditorNotebookNote({
          title: formTitle,
          content: formContent,
          category: formCategory,
          is_draft: formIsDraft,
          is_starred: false,
        });
        setNotes((prev) => [created, ...prev]);
        setSelectedNote(created);
      }
      setIsEditing(false);
    } catch {
      /* ignore */
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEditorNotebookNote(id);
      const remaining = notes.filter((n) => n.id !== id);
      setNotes(remaining);
      if (selectedNote?.id === id) {
        setSelectedNote(remaining[0] || null);
        setIsEditing(false);
      }
    } catch {
      /* ignore */
    }
  };

  const handleToggleStar = async (note: EditorNotebookNote) => {
    try {
      const updated = await updateEditorNotebookNote(note.id, {
        is_starred: !note.is_starred,
      });
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      if (selectedNote?.id === updated.id) {
        setSelectedNote(updated);
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <div data-testid="editor-notebook-overlay" className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Исследовательская память</small>
              <h2>Академический блокнот</h2>
              <p>Фиксация концепций, решений и протоколов</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn primary" onClick={handleStartCreate}>
              <PlusIcon className="w-3.5 h-3.5" />
              Создать запись
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div
          className="cab-body"
          style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: 14 }}
        >
          <aside className="cabinet-panel">
            <header>
              <div style={{ position: 'relative', width: '100%' }}>
                <SearchIcon
                  className="w-4 h-4"
                  style={{ position: 'absolute', left: 10, top: 12, opacity: 0.55 }}
                />
                <input
                  type="search"
                  className="cabinet-search"
                  style={{ width: '100%', paddingLeft: 32 }}
                  placeholder="Поиск по заметкам…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </header>
            <div className="cabinet-panel-body">
              <div className="cabinet-filters" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    className={`cabinet-filter${activeCategory === cat.key ? ' active' : ''}`}
                    onClick={() => setActiveCategory(cat.key)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="cabinet-stack">
                {loading ? (
                  <div className="cabinet-note">Загрузка…</div>
                ) : filteredNotes.length === 0 ? (
                  <div className="cabinet-note">Заметок не найдено</div>
                ) : (
                  filteredNotes.map((note) => {
                    const isSelected = selectedNote?.id === note.id;
                    return (
                      <button
                        key={note.id}
                        type="button"
                        className="cabinet-card"
                        style={
                          {
                            '--accent': isSelected ? '#41d2ff' : '#8faebb',
                            textAlign: 'left',
                            cursor: 'pointer',
                            width: '100%',
                          } as CSSProperties
                        }
                        onClick={() => {
                          setSelectedNote(note);
                          setIsEditing(false);
                        }}
                      >
                        <div className="cabinet-actions">
                          <span
                            className="cabinet-badge"
                            style={{ '--badge': '#8faebb' } as CSSProperties}
                          >
                            {note.category}
                          </span>
                          <span className="cabinet-spacer" />
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleToggleStar(note);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                void handleToggleStar(note);
                              }
                            }}
                            style={{ color: note.is_starred ? '#e2ad3a' : '#8faebb' }}
                          >
                            ★
                          </span>
                        </div>
                        <strong style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                          {note.title}
                        </strong>
                        <p
                          style={{
                            margin: '4px 0 0',
                            color: '#8faebb',
                            fontSize: 11,
                            lineHeight: 1.4,
                          }}
                        >
                          {note.content.slice(0, 100)}
                          {note.content.length > 100 ? '…' : ''}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>

          <main className="cabinet-panel">
            {isEditing ? (
              <form onSubmit={handleSave} className="cabinet-panel-body">
                <div className="cabinet-actions" style={{ marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 15 }}>
                    {selectedNote ? 'Редактировать запись' : 'Новая исследовательская запись'}
                  </h3>
                  <span className="cabinet-spacer" />
                  <button type="button" className="cabinet-btn" onClick={() => setIsEditing(false)}>
                    Отмена
                  </button>
                  <button type="submit" className="cabinet-btn primary">
                    Сохранить
                  </button>
                </div>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Категория</span>
                    <select
                      className="cabinet-select"
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                    >
                      <option value="STRATEGIC">Стратегические</option>
                      <option value="DECISIONS">Решения</option>
                      <option value="DRAFTS">Черновики</option>
                    </select>
                  </label>
                  <label className="cabinet-field" style={{ justifyContent: 'flex-end' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
                      <input
                        type="checkbox"
                        id="isDraft"
                        checked={formIsDraft}
                        onChange={(e) => setFormIsDraft(e.target.checked)}
                      />
                      Отметить как черновик
                    </span>
                  </label>
                </div>
                <label className="cabinet-field" style={{ marginTop: 10 }}>
                  <span>Заголовок</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Например: Протокол рецензирования монографии…"
                  />
                </label>
                <label className="cabinet-field" style={{ marginTop: 10 }}>
                  <span>Содержимое</span>
                  <textarea
                    className="cabinet-textarea"
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    placeholder="Опишите гипотезы, решения, аргументы или правки…"
                    rows={12}
                  />
                </label>
              </form>
            ) : selectedNote ? (
              <div className="cabinet-panel-body">
                <div className="cabinet-actions" style={{ marginBottom: 12 }}>
                  <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                    {selectedNote.category}
                  </span>
                  {selectedNote.is_draft ? (
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': '#e2ad3a' } as CSSProperties}
                    >
                      Черновик
                    </span>
                  ) : null}
                  <span style={{ fontSize: 11, color: '#8faebb' }}>
                    {new Date(selectedNote.created_at).toLocaleString('ru-RU')}
                  </span>
                  <span className="cabinet-spacer" />
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => handleStartEdit(selectedNote)}
                  >
                    Редактировать
                  </button>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => void handleDelete(selectedNote.id)}
                    aria-label="Удалить"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>{selectedNote.title}</h3>
                <div className="cabinet-note" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {selectedNote.content}
                </div>
              </div>
            ) : (
              <div className="cabinet-panel-body" style={{ textAlign: 'center', paddingTop: 48 }}>
                <h4 style={{ margin: '0 0 8px' }}>Выберите или создайте заметку</h4>
                <p style={{ color: '#8faebb', fontSize: 12, marginBottom: 16 }}>
                  Блокнот сохраняет конфиденциальные комментарии редактора, тезисы согласования и
                  рабочие мысли
                </p>
                <button type="button" className="cabinet-btn primary" onClick={handleStartCreate}>
                  Создать новую заметку
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
